import updateMailTipsForRecipients from '../actions/updateMailTipsForRecipients';
import ComposeViewState, { ComposeOperation } from '../store/schema/ComposeViewState';
import getRelativeComplementOfComposeRecipients from '../utils/getRelativeComplementOfComposeRecipients';
import isSmartComposeEnabled from '../utils/isSmartComposeEnabled';
import { refreshSharingTips } from '../utils/refreshSharingTips';
import { lazyTriggerRecipientsChangedEvent, RecipientFieldEnum } from 'owa-addins-core';
import { logUsage } from 'owa-analytics';
import attachmentDatapoints from 'owa-attachment-well-data/lib/datapoints';
import { updateRecipientsForSmartCompose } from 'owa-editor-smartcompose-plugin';
import { isFeatureEnabled } from 'owa-feature-flags';
import triggerPolicyTips from 'owa-mail-compose-store/lib/actions/triggerPolicyTips';
import { getCurrentTableMailboxInfo } from 'owa-mail-mailboxinfo';
import { getStore } from 'owa-mail-store/lib/store/Store';
import { isPolicyTipsEnabled } from 'owa-policy-tips/lib/utils/isPolicyTipsEnabled';
import addReadWriteRecipients from 'owa-readwrite-recipient-well/lib/actions/addReadWriteRecipients';
import onRecipientsChanged from 'owa-readwrite-recipient-well/lib/actions/onRecipientsChanged';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import removeRecipientFromRecipientWell from 'owa-recipient-common/lib/actions/removeRecipientFromRecipientWell';
import ReadWriteRecipientViewState from 'owa-recipient-common/lib/store/schema/ReadWriteRecipientViewState';
import ReadWriteRecipientWellViewState from 'owa-recipient-common/lib/store/schema/ReadWriteRecipientWellViewState';
import getReadWriteRecipientViewState from 'owa-recipient-common/lib/utils/getReadWriteRecipientViewState';
import EventTrigger from 'owa-service/lib/contract/EventTrigger';
import { orchestrator } from 'satcheljs';
import {
    infoBarAddHandler,
    infoBarRemoveHandler,
    infoBarGetIdsHandler,
} from '../utils/infoBarHandlers';
import {
    includeMostRecentAttachmentsInfoBarMessage,
    INCLUDE_MOST_RECENT_ATTACHMENTS_MESSAGEID,
    RecipientsCollection,
} from 'owa-attachment-well-data/lib/utils/includeMostRecentAttachmentsInfoBarMessage';

const NO_RECIPIENTS_MESSAGEID = 'errorMessageNoRecipentsMailCanNotBeSend';

// Exported for testing purposes only
export let composeViewStates: ComposeViewState[] = [];
// Exported for testing purposes only
export let composeViewStatesWithActedOnMostRecentAttachmentsMessage: ComposeViewState[] = [];

export function subscribeToRecipientsChangeEvent(viewState: ComposeViewState) {
    composeViewStates.push(viewState);
}

export function unsubscribeToRecipientsChangeEvent(viewState: ComposeViewState) {
    let index = composeViewStates.indexOf(viewState);
    composeViewStates.splice(index, 1);

    index = composeViewStatesWithActedOnMostRecentAttachmentsMessage.indexOf(viewState);
    if (index !== -1) {
        composeViewStatesWithActedOnMostRecentAttachmentsMessage.splice(index, 1);
    }
}

orchestrator(addReadWriteRecipients, actionMessage => {
    const { recipientData, recipientWell } = actionMessage;
    const recipients: ReadWriteRecipientViewState[] = recipientData.map(entry =>
        getReadWriteRecipientViewState(entry.newPersona, entry.isValid, entry.newEmailAddress)
    );

    if (isPolicyTipsEnabled()) {
        updatePolicyTips(recipientWell);
    }

    updateMailtips(recipientWell, recipients);
});

orchestrator(removeRecipientFromRecipientWell, actionMessage => {
    updateInfoBarMessages(actionMessage.recipientWellRecipients);

    if (isPolicyTipsEnabled()) {
        updatePolicyTips(actionMessage.recipientWellRecipients);
    }
});

function getRecipientType(
    viewState: ComposeViewState,
    recipientWellViewState: ReadWriteRecipientWellViewState
): RecipientFieldEnum {
    if (recipientWellViewState === viewState.toRecipientWell) {
        return RecipientFieldEnum.To;
    } else if (recipientWellViewState === viewState.ccRecipientWell) {
        return RecipientFieldEnum.Cc;
    } else if (recipientWellViewState === viewState.bccRecipientWell) {
        return RecipientFieldEnum.Bcc;
    }

    return null;
}

orchestrator(onRecipientsChanged, actionMessage => {
    const { recipientWellRecipients, recipientData } = actionMessage;
    if (recipientData) {
        const recipients: ReadWriteRecipientViewState[] = recipientData.map(entry =>
            getReadWriteRecipientViewState(entry.newPersona, entry.isValid, entry.newEmailAddress)
        );

        recipients.forEach(newRecipient => {
            updateInfoBarMessages(recipientWellRecipients, newRecipient.displayText);
        });
    }

    if (isFeatureEnabled('rp-addins')) {
        const viewState = getCurrentComposeViewState(actionMessage.recipientWellRecipients);
        if (viewState) {
            const type = getRecipientType(viewState, actionMessage.recipientWellRecipients);
            lazyTriggerRecipientsChangedEvent
                .import()
                .then(triggerRecipientsChangedEvent =>
                    triggerRecipientsChangedEvent(viewState.composeId, type)
                );
            refreshSharingTips(viewState);
        }
    }

    if (isSmartComposeEnabled()) {
        updateRecipientsForSmartCompose.importAndExecute(
            actionMessage.recipientWellRecipients.recipients
        );
    }
});

function updateMailtips(
    recipientWell: RecipientWellWithFindControlViewState,
    recipients: ReadWriteRecipientViewState[]
) {
    const composeViewState: ComposeViewState = getCurrentComposeViewState(recipientWell);
    if (!composeViewState) {
        return;
    }

    updateMailTipsForRecipients(composeViewState.composeId, recipientWell, recipients);
}

function updatePolicyTips(recipientWell: ReadWriteRecipientWellViewState) {
    const currentViewState: ComposeViewState = getCurrentComposeViewState(recipientWell);
    if (!currentViewState) {
        return;
    }

    triggerPolicyTips(currentViewState, EventTrigger.RecipientWell);
}

export function updateInfoBarMessages(
    recipientWell: ReadWriteRecipientWellViewState,
    newRecipientName?: string
) {
    const currentViewState: ComposeViewState = getCurrentComposeViewState(recipientWell);
    if (!currentViewState) {
        return;
    }

    const recipients: RecipientsCollection = initializeRecipientArrays(currentViewState);
    if (newRecipientName) {
        removeNoRecipientsError(currentViewState);
        updateIncludeMostRecentAttachmentsMessage(currentViewState, recipients, newRecipientName);
    } else {
        updateIncludeMostRecentAttachmentsMessage(currentViewState, recipients);
    }
}

export function getCurrentComposeViewState(
    recipientWell: ReadWriteRecipientWellViewState
): ComposeViewState {
    let currentViewState: ComposeViewState = null;
    let recipients: RecipientsCollection = null;
    if (recipientWell && recipientWell.recipients) {
        for (const viewState of composeViewStates) {
            recipients = initializeRecipientArrays(viewState);
            if (isCurrentCompose(recipients, recipientWell.recipients)) {
                currentViewState = viewState;
                break;
            }
        }
    }
    return currentViewState;
}

export function getComposeViewState(composeId: string): ComposeViewState {
    for (const viewState of composeViewStates) {
        if (viewState.composeId === composeId) {
            return viewState;
        }
    }

    return null;
}

function removeNoRecipientsError(viewState: ComposeViewState) {
    // VSO 28792: When adding a recipient, remove the no recipients error message if this exists
    infoBarRemoveHandler(viewState)([NO_RECIPIENTS_MESSAGEID]);
}

// Exported for testing purposes only
export function updateIncludeMostRecentAttachmentsMessage(
    viewState: ComposeViewState,
    recipients: RecipientsCollection,
    newRecipientName?: string
) {
    if (viewState.referenceItemId) {
        const item = getStore().items.get(viewState.referenceItemId.Id);
        if (item && !hasUserActedOnIncludeMostRecentAttachmentsMessage(viewState)) {
            const addedRecipientsInCompose: string[] = getRelativeComplementOfComposeRecipients(
                recipients,
                item
            );
            const numberOfAddedRecipientsInCompose: number = addedRecipientsInCompose.length;
            if (
                shouldIncludeMostRecentAttachmentsInfoBarMessage(
                    viewState,
                    item.HasAttachments,
                    numberOfAddedRecipientsInCompose
                )
            ) {
                logShowIncludeMostRecentAttachmentsMessageDatapoint(viewState);

                includeMostRecentAttachmentsInfoBarMessage(
                    { ...viewState.itemId, mailboxInfo: getCurrentTableMailboxInfo() },
                    item,
                    viewState.attachmentWell,
                    numberOfAddedRecipientsInCompose === 1
                        ? addedRecipientsInCompose[0]
                        : newRecipientName,
                    numberOfAddedRecipientsInCompose,
                    infoBarAddHandler(viewState),
                    infoBarRemoveHandler(viewState),
                    updateActedComposeHandler(viewState)
                );
            } else if (
                shouldRemoveMostRecentAttachmentsInfoBarMessage(
                    viewState,
                    numberOfAddedRecipientsInCompose
                )
            ) {
                infoBarRemoveHandler(viewState)([INCLUDE_MOST_RECENT_ATTACHMENTS_MESSAGEID]);
            }
        }
    }
}

// Exported for testing purposes only
export function initializeRecipientArrays(viewState: ComposeViewState): RecipientsCollection {
    return {
        toRecipients: viewState.toRecipientWell ? viewState.toRecipientWell.recipients : [],
        ccRecipients: viewState.ccRecipientWell ? viewState.ccRecipientWell.recipients : [],
        bccRecipients: viewState.bccRecipientWell ? viewState.bccRecipientWell.recipients : [],
    };
}

function isCurrentCompose(
    recipients: RecipientsCollection,
    currentRecipients: ReadWriteRecipientViewState[]
) {
    return (
        recipients.toRecipients === currentRecipients ||
        recipients.ccRecipients === currentRecipients ||
        recipients.bccRecipients === currentRecipients
    );
}

// Exported for testing purposes only
export function shouldIncludeMostRecentAttachmentsInfoBarMessage(
    viewState: ComposeViewState,
    parentMessageHasAttachments: boolean,
    numberOfDistinctRecipientsAdded: number
) {
    const composeOperation: ComposeOperation = viewState.operation;

    return (
        isFeatureEnabled('doc-attachment-includeMostRecentAttachments') &&
        parentMessageHasAttachments &&
        numberOfDistinctRecipientsAdded > 0 &&
        (composeOperation === ComposeOperation.Reply ||
            composeOperation === ComposeOperation.ReplyAll ||
            composeOperation === ComposeOperation.EditDraft)
    );
}

// Exported for testing purposes only
export function shouldRemoveMostRecentAttachmentsInfoBarMessage(
    viewState: ComposeViewState,
    numberOfDistinctRecipientsAdded: number
) {
    return (
        isFeatureEnabled('doc-attachment-includeMostRecentAttachments') &&
        isIncludeMostRecentAttachmentsInfoBarPresent(viewState) &&
        numberOfDistinctRecipientsAdded === 0
    );
}

function hasUserActedOnIncludeMostRecentAttachmentsMessage(viewState: ComposeViewState) {
    return composeViewStatesWithActedOnMostRecentAttachmentsMessage.indexOf(viewState) !== -1;
}

// Exported for testing reason only
export const updateActedComposeHandler = (viewState: ComposeViewState) => () => {
    composeViewStatesWithActedOnMostRecentAttachmentsMessage.push(viewState);
};

function isIncludeMostRecentAttachmentsInfoBarPresent(viewState: ComposeViewState) {
    const infoBarIds: string[] = infoBarGetIdsHandler(viewState)();
    return infoBarIds.indexOf(INCLUDE_MOST_RECENT_ATTACHMENTS_MESSAGEID) !== -1;
}

function logShowIncludeMostRecentAttachmentsMessageDatapoint(viewState: ComposeViewState) {
    if (!isIncludeMostRecentAttachmentsInfoBarPresent(viewState)) {
        logUsage(
            attachmentDatapoints.AttachmentShowIncludeMostRecentAttachmentsMessage.datapoint.name
        );
    }
}
